/**
 * Web checkout currency. Geo is a hint. The pay action only accepts TTD or USD
 * and locks the charge itself. Omitting currency keeps the wallet rail
 * (a blank country still pays with Wam).
 */

import {
  type BillingCurrency,
  type BillingRail,
  billingCurrencyFromCountry,
  billingRailFromCountry,
  isTrinidadCountry,
  usdCentsFromTtdCents,
} from "./billingRail";

export const GEO_COUNTRY_HEADERS = [
  "cf-ipcountry",
  "x-vercel-ip-country",
  "cloudfront-viewer-country",
  "x-country-code",
] as const;

type HeaderSource =
  | { get(name: string): string | null }
  | Record<string, string | null | undefined>;

function readHeader(headers: HeaderSource, name: string): string | null {
  if (typeof (headers as { get?: unknown }).get === "function") {
    return (headers as { get(name: string): string | null }).get(name);
  }
  const record = headers as Record<string, string | null | undefined>;
  return record[name] ?? record[name.toLowerCase()] ?? null;
}

/** Two-letter country, or null when the edge has no real country (XX, T1, blank). */
export function countryCodeFromGeoHeader(
  value: string | null | undefined,
): string | null {
  const code = (value ?? "").trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return null;
  if (code === "XX" || code === "T1") return null;
  return code;
}

export function countryFromGeoHeaders(headers: HeaderSource): string | null {
  for (const name of GEO_COUNTRY_HEADERS) {
    const code = countryCodeFromGeoHeader(readHeader(headers, name));
    if (code) return code;
  }
  return null;
}

export function regionFromLocale(locale: string | null | undefined): string | null {
  const tag = (locale ?? "").trim().replace(/_/g, "-");
  if (!tag) return null;
  try {
    const region = new Intl.Locale(tag).region;
    if (region && /^[A-Z]{2}$/.test(region)) return region;
  } catch {
    // Private-use tags throw. The regex below covers en-TT style tags.
  }
  const match = tag.match(/-([A-Za-z]{2})$/);
  return match ? match[1].toUpperCase() : null;
}

function currencyFromCountrySignal(country: string): BillingCurrency {
  const code = countryCodeFromGeoHeader(country);
  if (code) return code === "TT" ? "TTD" : "USD";
  return isTrinidadCountry(country) ? "TTD" : "USD";
}

/**
 * Suggested currency for the switch. Geo, then a non-blank wallet country,
 * then the browser locale region. Unknown is USD. A blank wallet is not
 * Trinidad here; that default stays on the server when currency is omitted.
 */
export function suggestCheckoutCurrency(input: {
  geoCountry?: string | null;
  walletCountry?: string | null;
  locale?: string | null;
}): BillingCurrency {
  const geo = (input.geoCountry ?? "").trim();
  if (geo && countryCodeFromGeoHeader(geo)) {
    return currencyFromCountrySignal(geo);
  }
  if (geo && !/^[A-Za-z]{2}$/.test(geo)) {
    return currencyFromCountrySignal(geo);
  }
  const wallet = (input.walletCountry ?? "").trim();
  if (wallet) return billingCurrencyFromCountry(wallet);
  const region = regionFromLocale(input.locale);
  if (region) return region === "TT" ? "TTD" : "USD";
  return "USD";
}

export function parseCheckoutCurrency(value: unknown): BillingCurrency | null {
  return value === "TTD" || value === "USD" ? value : null;
}

export function railForCheckoutCurrency(currency: BillingCurrency): BillingRail {
  return currency === "TTD" ? "wam" : "paypal";
}

export function resolveServerCheckoutRail(input: {
  currency?: string | null;
  walletCountry?: string | null;
}): { currency: BillingCurrency; rail: BillingRail } {
  const raw = input.currency;
  if (raw != null && String(raw).trim() !== "") {
    const currency = parseCheckoutCurrency(raw);
    if (!currency) throw new Error("Choose TTD or USD.");
    return { currency, rail: railForCheckoutCurrency(currency) };
  }
  return {
    currency: billingCurrencyFromCountry(input.walletCountry),
    rail: billingRailFromCountry(input.walletCountry),
  };
}

export function assertCheckoutRail(
  rail: BillingRail,
  input: { currency?: string | null; walletCountry?: string | null },
): BillingCurrency {
  const resolved = resolveServerCheckoutRail(input);
  if (resolved.rail !== rail) {
    throw new Error(
      resolved.rail === "wam"
        ? "This wallet pays with Wam in TTD."
        : "This checkout pays in USD with PayPal.",
    );
  }
  return resolved.currency;
}

/**
 * Display stand-in when the live USD→TTD cache is still empty.
 * `serverChargeMinor` does not read this. Pay still locks a live rate.
 * 6.8 is the rate the charge tests and checkout preview already use.
 */
export { FIXED_USD_TO_TTD } from "./fxUsdTtd";
import { FIXED_USD_TO_TTD as FIXED_RATE } from "./fxUsdTtd";
export const CHECKOUT_APPROX_USD_TO_TTD = FIXED_RATE;
export const CHECKOUT_FX_WAIT_MS = 3000;

/** UI conversion only. A missing cache stays blank until `waited`, then approx. */
export function checkoutDisplayRate(_input: {
  cached: number | null | undefined;
  waited: boolean;
}): { rate: number | null; approximate: boolean } {
  return { rate: FIXED_RATE, approximate: false };
}

/** Charge minor units. USD is the live FX lock. Never a client-supplied dollar amount. */
export function serverChargeMinor(
  currency: BillingCurrency,
  ttdCents: number,
  fxUsdToTtd: number,
): number {
  if (currency === "TTD") {
    if (!Number.isSafeInteger(ttdCents) || ttdCents < 1) {
      throw new Error("Invalid TTD amount");
    }
    return ttdCents;
  }
  return usdCentsFromTtdCents(ttdCents, fxUsdToTtd);
}
